<template>
  <section class="policy-shared-cases" aria-label="共享案例列表">
    <p class="lab-note">展示共享给当前账号的政策案例。接收者可以查看详情和体验，只有创建者可以修改、复制、共享或分配。</p>
    <operation-message :value="notice" :type="failed ? 'error' : 'success'" @consumed="notice = ''" />
    <button v-if="copiedCase && !failed" type="button" class="btn small" @click="$emit('configure-copy', copiedCase.id)">配置副本</button>
    <div v-if="loading" class="lab-empty" role="status">正在加载共享案例…</div>
    <div v-else-if="loadFailed" class="lab-empty">共享案例加载失败。<button type="button" class="btn small" @click="load()">重新加载</button></div>
    <div v-else-if="sharedCases.length" class="network-training-cards training-case-grid policy-shared-cases-list" :aria-busy="loading">
      <training-case-card v-for="(item, index) in visibleCases" :key="item.id" :data-case-id="item.id" platform="policy" category="共享案例" :theme-index="index">
        <div class="network-training-title-row"><h3>{{ item.name }}</h3><div class="network-training-badges"><span class="network-training-tag category">共享案例</span></div></div>
        <p class="network-training-description">创建人：{{ item.authorName || '—' }}</p><p class="network-training-meta">创建时间：{{ date(item.createdAt) }}</p>
        <div class="network-training-counts">措施 <b>{{ item.measureCount == null ? '—' : item.measureCount }}</b> 项</div>
        <div class="network-training-actions"><button type="button" class="btn small primary" :disabled="busy || actionBusy" @click="$emit('view', item.id)">查看</button><button type="button" class="btn small" :disabled="busy || actionBusy" @click="$emit('experience', item)">学生视角体验</button><button v-if="item.canCopy" type="button" class="btn small" :disabled="busy || actionBusy || !!copyReason(item)" :title="copyReason(item)" :aria-busy="copyId === item.id" @click="copy(item)">{{ copyId === item.id ? '正在复制…' : '复制到我的案例' }}</button></div>
      </training-case-card>
    </div>
    <list-pagination v-if="sharedCases.length" :page="currentPage" :page-size="pageSize" :total="sharedCases.length" @change="currentPage = $event" />
    <div v-if="!loading && !loadFailed && !sharedCases.length" class="lab-empty">暂无共享给您的案例</div>
  </section>
</template>
<script>
import { api, errorText } from '../services/api'
import TrainingCaseCard from './TrainingCaseCard.vue'
import ListPagination from './ListPagination.vue'

const base = '/policy/teacher-assist'
const requestId = () => 'policy-shared-' + (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : Date.now() + '-' + Math.random().toString(36).slice(2))
const hasRevision = item => item.revision != null && item.revision !== ''

export default {
  name: 'PolicySharedCases', components: { TrainingCaseCard, ListPagination },
  props: { active: Boolean, actionBusy: Boolean },
  data () { return { sharedCases: [], currentPage: 1, pageSize: 9, canCopy: false, loading: false, loadFailed: false, loadVersion: 0, disposed: false, copyId: '', copyRequests: {}, copiedCase: null, notice: '', failed: false } },
  computed: { busy () { return !!this.copyId }, visibleCases () { const start = (this.currentPage - 1) * this.pageSize; return this.sharedCases.slice(start, start + this.pageSize) } },
  created () { if (this.active) this.load() },
  beforeDestroy () { this.disposed = true; this.loadVersion++; this.$emit('busy-change', false) },
  watch: {
    active (value) { if (value && !this.busy && !Object.keys(this.copyRequests).length) this.load(true) },
    busy (value) { this.$emit('busy-change', value) },
    sharedCases () { this.ensurePage() }
  },
  methods: {
    async load (preserveNotice = false) {
      if (this.disposed) return false
      const version = ++this.loadVersion
      this.loading = true; this.loadFailed = false
      if (!preserveNotice) { this.notice = ''; this.failed = false }
      try {
        const data = await api.get(base)
        if (version !== this.loadVersion || this.disposed) return false
        if (!data || !Array.isArray(data.sharedCases)) throw new Error('共享案例列表返回不完整，请重新加载')
        this.sharedCases = data.sharedCases; this.ensurePage(); this.canCopy = data.canCopy === true
        return true
      } catch (error) {
        if (version !== this.loadVersion || this.disposed) return false
        this.sharedCases = []; this.ensurePage(); this.canCopy = false; this.loadFailed = true
        this.notice = (preserveNotice && this.notice ? this.notice + '；' : '') + '共享案例加载失败：' + errorText(error); this.failed = true
        return false
      } finally { if (version === this.loadVersion && !this.disposed) this.loading = false }
    },
    ensurePage () { const totalPages = Math.max(1, Math.ceil(this.sharedCases.length / this.pageSize)); if (this.currentPage > totalPages) this.currentPage = totalPages },
    copyReason (item) {
      if (this.loading || this.loadFailed) return '请等待共享案例列表成功加载'
      if (!this.canCopy) return '当前账号未获案例复制权限'
      if (item.canCopy !== true) return '该共享案例当前不可复制，请刷新列表'
      if (!hasRevision(item)) return '共享案例修订信息缺失，请重新加载'
      return ''
    },
    async copy (item) {
      if (this.busy || this.loading || this.disposed) return
      const source = this.sharedCases.find(value => value.id === item.id)
      const reason = source ? this.copyReason(source) : '共享案例已变更，请重新加载'
      if (reason) { this.notice = reason; this.failed = true; return }
      this.copyId = source.id; this.failed = false; this.copiedCase = null
      const pending = this.copyRequests[source.id]
      const body = pending || { revision: source.revision, requestId: requestId() }
      this.$set(this.copyRequests, source.id, body)
      this.notice = pending ? '正在核对上次复制结果，重试将复用同一请求。' : ''
      try {
        const data = await api.post(base + '/shared-cases/' + encodeURIComponent(source.id) + '/copy', body)
        if (this.disposed) return
        if (!data || !data.case || !data.case.id) throw new Error('未返回有效副本，请重试以确认复制结果')
        this.$delete(this.copyRequests, source.id); this.copiedCase = data.case
        this.$emit('copied', data.case)
        this.notice = '已将“' + data.case.name + '”复制到自定义案例列表。'
        await this.load(true)
      } catch (error) {
        if (this.disposed) return
        const status = error.response && error.response.status
        if (status === 403 || status === 409) this.$delete(this.copyRequests, source.id)
        this.notice = '复制案例失败：' + errorText(error); this.failed = true
        if (status === 403 || status === 409) await this.load(true)
        if (!status || status === 408 || status >= 500) this.notice += '；复制结果尚未确认，请重试，重试不会重复创建副本。'
      } finally { if (!this.disposed) this.copyId = '' }
    },
    date (value) {
      if (!value) return '—'
      const date = new Date(value); if (!Number.isFinite(date.getTime())) return String(value)
      const pad = number => String(number).padStart(2, '0')
      return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds())
    }
  }
}
</script>
